import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings as SettingsIcon, LogOut, Bell, Search, Radio, Menu, X, LayoutDashboard, Wand2, BarChart3, HardDrive, Stethoscope, ListChecks, Sun, Moon } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { authAPI } from '../lib/api';
import { APP_VERSION } from '../lib/version';
import useGlobalAlarmWatcher from '../hooks/useGlobalAlarmWatcher';
import useOltStatusWatcher from '../hooks/useOltStatusWatcher';

// Swipe order for phone gesture navigation (module scope — estático, não recria a cada render)
const SWIPE_ORDER = ['/app', '/app/unconfigured', '/app/configured', '/app/graphs', '/app/events', '/app/diagnostics', '/app/tasks'];

export default function AppLayout({ children }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { theme, whitelabel, t, lang, toggleLang, mode, toggleMode, user, logout, hasPermission, refreshPermissions } = useApp();

  // Refresh dinâmico das permissões efetivas:
  //   - a cada troca de rota (pega mudanças feitas pelo admin em runtime)
  //   - quando a janela ganha foco de volta
  useEffect(() => {
    if (user?.id) refreshPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  useEffect(() => {
    const onFocus = () => { if (user?.id) refreshPermissions(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const [drawer, setDrawer] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const primary = theme?.primary || '#2563eb';

  // Watcher global de alarmes — toca sons configurados em qualquer tela.
  useGlobalAlarmWatcher(!!user);
  useOltStatusWatcher(!!user);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Custom lightweight swipe detector using pointer events (does not interfere with clicks/scrolls)
  const mainRef = React.useRef(null);
  useEffect(() => {
    if (!isMobile) return;
    const el = mainRef.current;
    if (!el) return;
    let startX = 0, startY = 0, startT = 0, tracking = false;
    const start = (e) => {
      const p = e.touches ? e.touches[0] : e;
      startX = p.clientX;
      startY = p.clientY;
      startT = Date.now();
      tracking = true;
    };
    const end = (e) => {
      if (!tracking) return;
      tracking = false;
      const p = e.changedTouches ? e.changedTouches[0] : e;
      const dx = p.clientX - startX;
      const dy = p.clientY - startY;
      const dt = Date.now() - startT;
      if (dt > 700) return;
      if (Math.abs(dx) < 80) return;
      if (Math.abs(dy) > Math.abs(dx) * 0.6) return; // reject dominantly vertical
      const idx = SWIPE_ORDER.indexOf(loc.pathname);
      if (idx === -1) return;
      if (dx < 0 && idx < SWIPE_ORDER.length - 1) navigate(SWIPE_ORDER[idx + 1]);
      else if (dx > 0 && idx > 0) navigate(SWIPE_ORDER[idx - 1]);
    };
    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchend', end, { passive: true });
    el.addEventListener('pointerdown', start);
    el.addEventListener('pointerup', end);
    return () => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchend', end);
      el.removeEventListener('pointerdown', start);
      el.removeEventListener('pointerup', end);
    };
  }, [isMobile, loc.pathname, navigate]);

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (_) {}
    logout();
    navigate('/login');
  };

  const userInitials = user
    ? (user.name || user.email || '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : 'AD';
  const userDisplayName = user?.name || user?.email || 'Admin';
  const userRole = user?.role || 'owner';

  const MENU = [
    { label: t.app.nav.dashboard, href: '/app', key: 'dashboard', perm: 'dashboard:read' },
    { label: t.app.nav.unconfigured, href: '/app/unconfigured', key: 'unconfigured', perm: 'onus:read' },
    { label: t.app.nav.configured, href: '/app/configured', key: 'configured', perm: 'onus:read' },
    { label: t.app.nav.graphs, href: '/app/graphs', key: 'graphs', perm: 'dashboard:read' },
    { label: t.app.nav.events, href: '/app/events', key: 'events', perm: 'alerts:read' },
    { label: t.app.nav.diagnostics, href: '/app/diagnostics', key: 'diagnostics', perm: 'alerts:read' },
    { label: t.app.nav.tasks, href: '/app/tasks', key: 'tasks', perm: 'tasks:read' },
    { label: t.app.nav.reports, href: '/app/reports', key: 'reports', perm: 'reports:read' },
    { label: t.app.nav.settings, href: '/app/settings', key: 'settings', perm: 'settings:read' },
  ].filter((m) => hasPermission(m.perm));

  const flag = lang === 'pt' ? '🇧🇷' : lang === 'es' ? '🇪🇸' : '🇺🇸';

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text-primary)]">
      <header className="relative z-40">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(1200px 380px at 20% -20%, ${primary}22 0%, transparent 60%),
                         radial-gradient(900px 320px at 90% -10%, ${primary}18 0%, transparent 60%)`,
          }}
        />
        <div className="relative max-w-none w-full px-4 md:px-6 pt-4 md:pt-5">
          <div className="rounded-3xl border border-[var(--border-mid)] bg-[var(--surface)]/85 backdrop-blur-md shadow-[0_10px_40px_-20px_rgba(0,0,0,0.25)]">
            <div className="flex items-center justify-between px-3 md:px-6 h-14 md:h-16 gap-2 md:gap-3">
              {/* Mobile hamburger */}
              <button
                onClick={() => setDrawer(true)}
                aria-label="menu"
                data-testid="mobile-menu-btn"
                className="2xl:hidden h-9 w-9 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--surface-2)] transition-colors"
              >
                <Menu size={18} />
              </button>

              <Link to="/app" data-testid="brand-logo" className="flex items-center gap-2.5 min-w-0 shrink-0">
                {whitelabel?.logo ? (
                  <img src={whitelabel.logo} alt={whitelabel.name} className="h-7 md:h-8 w-auto object-contain rounded-lg" />
                ) : (
                  <div
                    className="h-8 w-8 md:h-9 md:w-9 rounded-2xl flex items-center justify-center text-white font-black shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}bb 100%)` }}
                  >
                    <Radio size={15} />
                  </div>
                )}
                <div className="font-display font-black text-sm md:text-base tracking-tight truncate">
                  {whitelabel?.name || 'OLT MANAGER'}
                </div>
              </Link>

              <nav className="hidden 2xl:flex items-center gap-0.5 relative">
                {MENU.filter((m) => m.key !== 'settings').map((m) => {
                  const active = loc.pathname === m.href || (m.key === 'reports' && loc.pathname.startsWith('/app/reports'));
                  return (
                    <Link
                      key={m.href}
                      to={m.href}
                      data-testid={`topnav-${m.key}`}
                      className="relative px-2.5 py-2 rounded-full text-[11px] uppercase tracking-wider font-bold whitespace-nowrap transition-colors"
                      style={{ color: active ? 'white' : 'var(--text-secondary)' }}
                    >
                      {active && (
                        <motion.span
                          layoutId="nav-pill"
                          className="absolute inset-0 rounded-full shadow-sm"
                          style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` }}
                          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                        />
                      )}
                      <span className="relative z-10">{m.label}</span>
                    </Link>
                  );
                })}

                {hasPermission('settings:read') && (
                  <Link
                    to="/app/settings"
                    data-testid="topnav-settings"
                    className="px-2.5 py-2 rounded-full text-[11px] uppercase tracking-wider font-bold whitespace-nowrap text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-2)] transition-colors inline-flex items-center gap-1.5"
                  >
                    <SettingsIcon size={12} /> {t.app.nav.settings}
                  </Link>
                )}
              </nav>

              <div className="flex items-center gap-1 md:gap-1.5 shrink-0">
                <button
                  onClick={toggleMode}
                  aria-label="toggle dark mode"
                  data-testid="app-theme-toggle"
                  title={mode === 'light' ? 'Modo escuro' : 'Modo claro'}
                  className="inline-flex h-9 w-9 rounded-full items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)] transition-colors"
                >
                  {mode === 'light' ? <Moon size={15} /> : <Sun size={15} />}
                </button>
                <button
                  onClick={toggleLang}
                  aria-label="toggle language"
                  data-testid="app-lang-toggle"
                  title={lang === 'pt' ? 'Português' : lang === 'es' ? 'Español' : 'English'}
                  className="inline-flex items-center gap-1 md:gap-1.5 h-9 px-2.5 md:px-3 rounded-full text-[10px] uppercase tracking-widest font-bold border border-[var(--border-mid)] hover:border-[var(--theme-primary)] transition-colors"
                >
                  <span className="text-sm leading-none">{flag}</span>
                  <span className="hidden sm:inline">{lang.toUpperCase()}</span>
                </button>
                <button
                  aria-label="search"
                  className="hidden md:inline-flex h-9 w-9 rounded-full items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <Search size={15} />
                </button>
                <button
                  aria-label="notifications"
                  className="relative inline-flex h-9 w-9 rounded-full items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <Bell size={15} />
                  <span
                    className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full ring-2 ring-[var(--surface)]"
                    style={{ background: primary }}
                  />
                </button>
                <div className="hidden md:flex items-center gap-2 pl-2 ml-1 border-l border-[var(--border-soft)]">
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-black shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}99 100%)` }}
                  >
                    {userInitials}
                  </div>
                  <div className="hidden 2xl:block max-w-[9rem] leading-tight">
                    <div className="text-xs font-semibold truncate">{userDisplayName}</div>
                    <div className="text-[10px] text-[var(--text-muted)] truncate">{userRole}</div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="ml-1 inline-flex items-center gap-1 h-8 rounded-full px-3 text-[10px] uppercase tracking-widest font-bold border border-[var(--border-mid)] hover:border-red-400 hover:text-red-400 transition-colors"
                  >
                    <LogOut size={11} /> {t.app.nav.log_out}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawer(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] 2xl:hidden"
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 32 }}
              className="fixed top-0 left-0 bottom-0 w-72 max-w-[85%] bg-[var(--surface)] z-[95] 2xl:hidden overflow-y-auto"
              data-testid="mobile-drawer"
            >
              <div className="p-5 border-b border-[var(--border-soft)] flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div
                    className="h-9 w-9 rounded-2xl flex items-center justify-center text-white font-black shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}bb 100%)` }}
                  >
                    <Radio size={15} />
                  </div>
                  <div className="font-display font-black text-base tracking-tight truncate">
                    {whitelabel?.name || 'OLT MANAGER'}
                  </div>
                </div>
                <button
                  onClick={() => setDrawer(false)}
                  aria-label="close menu"
                  className="h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--surface-2)]"
                >
                  <X size={16} />
                </button>
              </div>
              <nav className="p-3 space-y-1">
                {MENU.map((m) => {
                  const active = loc.pathname === m.href
                    || (m.key === 'settings' && loc.pathname.startsWith('/app/settings'))
                    || (m.key === 'reports' && loc.pathname.startsWith('/app/reports'));
                  return (
                    <Link
                      key={m.href}
                      to={m.href}
                      onClick={() => setDrawer(false)}
                      data-testid={`mobile-nav-${m.key}`}
                      className={`block px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${active ? 'text-white' : 'text-[var(--text-primary)] hover:bg-[var(--surface-2)]'}`}
                      style={active ? { background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` } : {}}
                    >
                      {m.label}
                    </Link>
                  );
                })}
                <div className="pt-3 mt-3 border-t border-[var(--border-soft)]">
                  <button
                    onClick={() => { setDrawer(false); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-[var(--text-secondary)] hover:bg-red-500/10 hover:text-red-400 transition-colors"
                  >
                    <LogOut size={14} /> {t.app.nav.log_out}
                  </button>
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main
        ref={mainRef}
        data-testid="app-main-swipe"
        className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8 pb-24 md:pb-8"
      >
        {children}
      </main>

      {/* Bottom nav (phone only) — snap-scroll horizontal for 4+ items */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-[var(--border-mid)] bg-[var(--surface)]/95 backdrop-blur-md"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
          {[
            { icon: LayoutDashboard, label: t.app.nav.dashboard, href: '/app', key: 'dashboard', perm: 'dashboard:read' },
            { icon: Wand2, label: t.app.nav.unconfigured, href: '/app/unconfigured', key: 'unconfigured', perm: 'onus:read' },
            { icon: HardDrive, label: t.app.nav.configured, href: '/app/configured', key: 'configured', perm: 'onus:read' },
            { icon: BarChart3, label: t.app.nav.graphs, href: '/app/graphs', key: 'graphs', perm: 'dashboard:read' },
            { icon: ListChecks, label: t.app.nav.tasks, href: '/app/tasks', key: 'tasks', perm: 'tasks:read' },
            { icon: SettingsIcon, label: t.app.nav.settings, href: '/app/settings', key: 'settings', perm: 'settings:read' },
          ].filter((it) => hasPermission(it.perm)).map((it) => {
            const active = loc.pathname === it.href || (it.key === 'settings' && loc.pathname.startsWith('/app/settings'));
            const Icon = it.icon;
            return (
              <Link
                key={it.key}
                to={it.href}
                data-testid={`bottom-nav-${it.key}`}
                className="relative flex flex-col items-center justify-center gap-1 py-2.5 min-w-[22%] snap-start transition-colors"
                style={{ color: active ? primary : 'var(--text-secondary)' }}
              >
                {active && (
                  <motion.span
                    layoutId="bottom-nav-indicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-10 rounded-b-full"
                    style={{ background: primary }}
                    transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                  />
                )}
                <Icon size={19} strokeWidth={active ? 2.2 : 1.8} />
                <span className="text-[9px] font-semibold uppercase tracking-widest truncate max-w-full px-1">{it.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <footer className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 pb-24 md:pb-6 text-[10px] uppercase tracking-widest text-[var(--text-muted)] text-center">
        {whitelabel?.name || 'OLT Manager'} <span className="text-[var(--theme-primary)]">v{APP_VERSION} · Beta</span> · © 2026 · <Link to="/" className="hover:text-[var(--text-primary)]">← back to landing</Link>
      </footer>
    </div>
  );
}
